import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaCoins, FaSpinner, FaPlus, FaTrash } from "react-icons/fa";
import apiRequest from "../services/api";

const OPENING_ACCOUNTS = [
  { code: "CASH", type: "ASSET" },
  { code: "BANK", type: "ASSET" },
  { code: "ACCOUNTS_RECEIVABLE", type: "ASSET" },
  { code: "INVENTORY", type: "ASSET" },
  { code: "EMPLOYEE_ADVANCE_RECEIVABLE", type: "ASSET" },
  { code: "REFUND_RECEIVABLE", type: "ASSET" },
  { code: "ACCUMULATED_DEPRECIATION", type: "ASSET" },

  { code: "ACCOUNTS_PAYABLE", type: "LIABILITY" },
  { code: "INCOME_TAX_PAYABLE", type: "LIABILITY" },
  { code: "EMPLOYEE_DEDUCTION_PAYABLE", type: "LIABILITY" },
  { code: "LOAN_PAYABLE", type: "LIABILITY" },

  { code: "OWNER_CAPITAL", type: "EQUITY" },
  { code: "RETAINED_EARNINGS", type: "EQUITY" },
];

export default function OpeningBalanceModal({
  isOpen,
  onClose,
  onSuccess,
  title = "Opening Balance",
}) {
  const [lines, setLines] = useState([{ accountCode: "", amount: "" }]);

  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setLines([{ accountCode: "", amount: "" }]);
    setNote("");
    setError("");
    setIsLoading(false);
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const addLine = () => {
    setLines((prev) => [...prev, { accountCode: "", amount: "" }]);
  };

  const removeLine = (index) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = (index, field, value) => {
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );
  };

  const getAccountType = (code) =>
    OPENING_ACCOUNTS.find((a) => a.code === code)?.type;

  const totalDebit = lines.reduce((sum, line) => {
    const amount = Number(line.amount || 0);
    return getAccountType(line.accountCode) === "ASSET" ? sum + amount : sum;
  }, 0);

  const totalCredit = lines.reduce((sum, line) => {
    const amount = Number(line.amount || 0);
    return ["LIABILITY", "EQUITY"].includes(getAccountType(line.accountCode))
      ? sum + amount
      : sum;
  }, 0);

  const difference = totalDebit - totalCredit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!lines.length) {
      setError("At least one opening balance account is required.");
      return;
    }

    const seen = new Set();

    for (const line of lines) {
      if (!line.accountCode) {
        setError("Please select an account for every line.");
        return;
      }

      if (seen.has(line.accountCode)) {
        setError(`Account ${line.accountCode} is entered more than once.`);
        return;
      }

      seen.add(line.accountCode);

      const amount = Number(line.amount);

      if (!amount || amount <= 0) {
        setError(`Amount must be greater than 0.00 for ${line.accountCode}.`);
        return;
      }
    }

    if (Math.abs(difference) > 0.01) {
      setError(
        `Opening balance is not balanced. Debit: ${totalDebit.toFixed(
          2
        )}, Credit: ${totalCredit.toFixed(2)}.`
      );
      return;
    }

    const payload = {
      requests: lines.map((line) => ({
        accountCode: line.accountCode,
        amount: Number(line.amount),
      })),
      note: note.trim() || null,
    };

    setIsLoading(true);

    try {
      const query = note.trim()
        ? `?note=${encodeURIComponent(note.trim())}`
        : "";

      await apiRequest(`reports/opening-balance${query}`, {
        method: "POST",
        body: JSON.stringify(
          lines.map((line) => ({
            accountCode: line.accountCode,
            amount: Number(line.amount),
          }))
        ),
        headers: {
          "Content-Type": "application/json",
        },
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Opening balance posting failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" />

      <div
        className="relative w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[90vh]
                   bg-slate-900 border border-white/10
                   rounded-t-2xl sm:rounded-2xl
                   shadow-2xl overflow-y-auto safe-area-pb"
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur
                     flex items-start justify-between gap-3
                     px-4 py-3 sm:px-5 sm:py-4
                     border-b border-white/5"
        >
          <div>
            <h3 className="text-sm sm:text-base font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <FaCoins className="text-amber-400" />
              {title}
            </h3>

            <p className="text-[10px] sm:text-[11px] text-slate-400 mt-1">
              Enter the opening balances as of the beginning of the accounting
              period.
            </p>
          </div>

          <button
            type="button"
            disabled={isLoading}
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-4 py-4 sm:px-5 sm:py-5 space-y-4"
        >
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium">
              {error}
            </div>
          )}

          {/* Lines */}
          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-[1fr_180px_40px] gap-2 px-1">
              <label className="text-[10px] uppercase font-bold text-slate-400">
                Account
              </label>

              <label className="text-[10px] uppercase font-bold text-slate-400 text-right">
                Opening Balance
              </label>
            </div>

            {lines.map((line, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_120px_36px] sm:grid-cols-[1fr_180px_40px] gap-2"
              >
                <select
                  required
                  disabled={isLoading}
                  value={line.accountCode}
                  onChange={(e) =>
                    updateLine(index, "accountCode", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500/40"
                >
                  <option value="">Select account</option>

                  <optgroup label="Assets">
                    {OPENING_ACCOUNTS.filter((a) => a.type === "ASSET").map(
                      (account) => (
                        <option key={account.code} value={account.code}>
                          {account.code}
                        </option>
                      )
                    )}
                  </optgroup>

                  <optgroup label="Liabilities">
                    {OPENING_ACCOUNTS.filter((a) => a.type === "LIABILITY").map(
                      (account) => (
                        <option key={account.code} value={account.code}>
                          {account.code}
                        </option>
                      )
                    )}
                  </optgroup>

                  <optgroup label="Equity">
                    {OPENING_ACCOUNTS.filter((a) => a.type === "EQUITY").map(
                      (account) => (
                        <option key={account.code} value={account.code}>
                          {account.code}
                        </option>
                      )
                    )}
                  </optgroup>
                </select>

                <input
                  required
                  disabled={isLoading}
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={line.amount}
                  onChange={(e) => updateLine(index, "amount", e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white text-right font-mono focus:outline-none focus:border-amber-500/40"
                />

                <button
                  type="button"
                  disabled={isLoading || lines.length === 1}
                  onClick={() => removeLine(index)}
                  className="flex items-center justify-center rounded-xl text-slate-500 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30"
                >
                  <FaTrash className="text-xs" />
                </button>
              </div>
            ))}

            <button
              type="button"
              disabled={isLoading}
              onClick={addLine}
              className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 text-slate-300 text-xs font-semibold hover:bg-white/10 transition"
            >
              <FaPlus />
              Add Account
            </button>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-3 border-t border-white/5">
            <div className="bg-slate-950 rounded-xl p-3">
              <div className="text-[10px] uppercase font-bold text-slate-500">
                Total Debit
              </div>
              <div className="text-sm font-bold text-white font-mono mt-1">
                {totalDebit.toFixed(2)}
              </div>
            </div>

            <div className="bg-slate-950 rounded-xl p-3">
              <div className="text-[10px] uppercase font-bold text-slate-500">
                Total Credit
              </div>
              <div className="text-sm font-bold text-white font-mono mt-1">
                {totalCredit.toFixed(2)}
              </div>
            </div>

            <div className="bg-slate-950 rounded-xl p-3">
              <div className="text-[10px] uppercase font-bold text-slate-500">
                Difference
              </div>
              <div
                className={`text-sm font-bold font-mono mt-1 ${
                  Math.abs(difference) <= 0.01
                    ? "text-emerald-400"
                    : "text-red-400"
                }`}
              >
                {difference.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] uppercase font-bold text-slate-400">
              Note
            </label>

            <textarea
              disabled={isLoading}
              rows={2}
              placeholder="Opening balance note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-white resize-none focus:outline-none focus:border-amber-500/40"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-3 border-t border-white/5">
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-white/5 text-slate-300 text-xs font-semibold hover:bg-white/10 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                isLoading || !lines.length || Math.abs(difference) > 0.01
              }
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold flex items-center justify-center gap-2 shadow-md transition disabled:opacity-60"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Posting…
                </>
              ) : (
                <>
                  <FaCoins />
                  Post Opening Balance
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
